import { object, func, node } from "prop-types";
import { observer } from "mobx-react";
import { BreakdownColumns } from "@repo/utils";
import { Checkbox } from "@components";
import { Grid, IconButton, Tooltip } from "@material-ui/core";
import { Pin, Trash } from "@assets";
import { useTranslation } from "react-i18next";
import classnames from "classnames";
import { makeStyles } from "@material-ui/styles";
import { DescriptionTitleEditor } from "@tools";

export const ColumnSettingEntry = observer(({
  columnSettings,
  onVisibilityChange,
  onPin,
  onRemove,
  children,
}) => {
  const { t } = useTranslation();
  const classes = useStyle();
  
  const { id, name, displayName, visible, forcedPin, pinnable, removable, applyPin, pinRight } = columnSettings;
  
  const handleVisibilityChange = () => {
    if(columnSettings.setVisibility)
      columnSettings.setVisibility(!visible);
    else
      onVisibilityChange(id, !visible);
  }
  
  const handlePin = () => {
    if(!pinnable)
      return;
    onPin(id, !applyPin, pinRight);
  }
  
  const handleRemove = () => {
    onRemove?.(id, displayName);
  }
  
  return (
    <Grid
      item container
      wrap="nowrap"
      alignItems="center"
      justifyContent="space-between"
    >
      <Grid item container alignItems="center" >
        <Checkbox
          name={ name }
          aria-label={ name }
          checked={ visible }
          onChange={ handleVisibilityChange }
          className="mr-2"
          disabled={name === BreakdownColumns.TITLE}
        />
        {
          pinnable
            ? <Checkbox
              name={ name }
              aria-label={ name }
              checked={ applyPin }
              onChange={ handlePin }
              className={classnames("mr-2 border-none bg-transparent", classes.pinCheckbox)}
              checkedIcon={<Pin className="color-primary" />}
              uncheckedIcon={<Pin className="transparent color-primary preview" />}
            />
            : <Tooltip title={t(`views.editor.breakdown_table.${forcedPin ? "un" : ""}pin_block`)}>
            <span className="mr-2">
              <Checkbox
                name={ name }
                aria-label={ name }
                checked={ forcedPin }
                className={classnames("cursor-arrow border-none bg-transparent", classes.pinCheckbox)}
                disabled={forcedPin}
                checkedIcon={<Pin className="text-lighter" />}
                uncheckedIcon={(
                  <Pin className={classnames(
                    "transparent",
                    !forcedPin ? "color-error" : "color-primary",
                    !forcedPin && "preview-error"
                  )} />
                )}
              />
            </span>
            </Tooltip>
          
        }
        {
          columnSettings.descriptionId
            ? <DescriptionTitleEditor
              columnSettings={columnSettings}
              className={classnames(classes.titleWrapper, "text-md")}
              inputClassName={classes.title}
            />
            : (
              <p className={classnames(classes.title, "text-md")}>
                { displayName || t(`views.editor.breakdown_table.columns.${ name }`) }
              </p>
            )
        }
        {
          removable &&
          <IconButton
            color="secondary"
            className="ml-2"
            onClick={handleRemove}
          >
            <Trash className="color-error text-md" />
          </IconButton>
        }
      </Grid>
      { children }
    </Grid>
  )
})

ColumnSettingEntry.propTypes = {
  columnSettings: object.isRequired,
  onVisibilityChange: func.isRequired,
  onPin: func.isRequired,
  onRemove: func.isRequired,
  children: node,
}

const useStyle = makeStyles(() => ({
  pinCheckbox: {
    "&:hover .preview": {
      opacity: "1 !important",
    },
    "&:hover .preview-error": {
      opacity: "0.8 !important",
    },
    // "&:not(:hover) .preview": {
    //   opacity: "0.2 !important",
    // },
  },
  title: {
    maxWidth: 148,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  titleWrapper: {
    maxWidth: 148,
  }
  
}));